import type { Channel } from 'api/Channels/types'
import type { GroupResponse } from 'api/Group/types'
import classNames from 'classnames'
import CommonIcon from 'CommonIcon'
import type { DetailsData } from 'data/types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { countryShortNameToString, thousandsSeparator } from 'utils'
import { useTextFit } from 'utils/useTextFit'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import ComparedValue from './ComparedValue'
import { DetailsType } from './constants'
import DetailsTypeBadge from './DetailsTypeBadge'
import useStyles from './useStyles'
import { getPercent } from './utils'

interface Props {
  countryId?: string
  data: DetailsData
  detailsType: DetailsType
  label: string
  hideFlag?: boolean
}

function DetailsMain({ countryId, data, detailsType, hideFlag = false, label }: Props) {
  const { t: translate } = useTranslation()
  const classes = useStyles({ detailsType })
  const refTextFit = useTextFit({ maxSize: 1.5 })
  const groupData = data as GroupResponse
  const channelData = data as Channel
  const channelFlagName = !hideFlag && countryShortNameToString(countryId, '')
  const channelFlag = countryId && channelFlagName ? <CommonIcon name={channelFlagName} /> : null
  const channelLogoName = channelData.logo ? <CommonIcon name={channelData.logo} className={classes.logo} /> : label
  const showShare = groupData.shareCurrent !== null && groupData.shareCurrent > 0
  const shopIcon = <Box className={classNames('fm fm-shop', classes.storesCountIcon)} mr={0.25} />

  return (
    <Grid item xs={3}>
      <Box className={classes.title}>
        <div>
          <Box display="flex" justifyContent={channelFlag ? 'flex-start' : 'center'}>
            <Box alignItems="center" display="flex">
              {channelFlag}
            </Box>
            <Box ref={refTextFit} maxHeight="2.5rem" textAlign="center" fontWeight={channelFlag ? '700' : 'inherit'}>
              {channelLogoName}
            </Box>
          </Box>
          {detailsType === DetailsType.DEFAULT && showShare && (
            <Grid container display="flex" spacing="0.25rem">
              <Grid item xs={6} textAlign="right">
                {getPercent(groupData.shareCurrent, translate)}
              </Grid>
              <Grid item xs={6} className={classes.shareCompared}>
                {getPercent(groupData.shareCompared, translate)}
              </Grid>
            </Grid>
          )}
          <DetailsTypeBadge detailsType={detailsType} percent={data.revenuePlan} />
        </div>
        {DetailsType.DEFAULT === detailsType && (
          <div>
            {Boolean(data.storesCurrentNECom) && data.storesCurrentNECom! > 0 && (
              <Box>
                {shopIcon}
                {thousandsSeparator(data.storesCurrentNECom)}
                <ComparedValue value={data.storesComparedNECom} />
              </Box>
            )}
            {Boolean(data.storesCurrentECom) && data.storesCurrentECom! > 0 && (
              <Box>
                <Box className={classNames('fm fm-at', classes.storesCountIcon)} mr={0.25} />
                {thousandsSeparator(data.storesCurrentECom)}
                <ComparedValue value={data.storesComparedECom} />
              </Box>
            )}
          </div>
        )}
        {DetailsType.BUDGET === detailsType && (
          <div>
            {Boolean(data.storesCurrentNECom) && data.storesCurrentNECom! > 0 && (
              <Box>
                {shopIcon}
                {thousandsSeparator(data.storesCurrentNECom)}
              </Box>
            )}
            {Boolean(data.storesCurrentECom) && data.storesCurrentECom! > 0 && (
              <Box>
                <Box className={classNames('fm fm-at', classes.storesCountIcon)} mr={0.25} />
                {thousandsSeparator(data.storesCurrentECom)}
              </Box>
            )}
          </div>
        )}
        {detailsType === DetailsType.L4L && (
          <div>
            {Boolean(data.storesCurrentL4L || data.storesComparedL4L) && (
              <Box>
                {shopIcon}
                {thousandsSeparator(data.storesCurrentL4L)}
                <ComparedValue value={data.storesComparedL4L} />
              </Box>
            )}
          </div>
        )}
        {detailsType === DetailsType.RETAIL && (
          <div>
            {Boolean(data.storesCurrentNECom || data.storesComparedNECom) && (
              <Box>
                {shopIcon}
                {thousandsSeparator(data.storesCurrentNECom)}
                <ComparedValue value={data.storesComparedNECom} />
              </Box>
            )}
          </div>
        )}
        {detailsType === DetailsType.ECOM && (
          <div>
            {Boolean(data.storesCurrentECom || data.storesComparedECom) && (
              <Box>
                <Box className={classNames('fm fm-at', classes.storesCountIcon)} mr={0.25} />
                {thousandsSeparator(data.storesCurrentECom)}
                <ComparedValue value={data.storesComparedECom} />
              </Box>
            )}
          </div>
        )}
      </Box>
    </Grid>
  )
}

export default DetailsMain
